/**
 * Client-side collection filters for AD Parfumerie.
 * Bypasses Shopify's storefront filter limitations by fetching the collection's
 * /products.json endpoint and filtering rendered cards by handle on URL change.
 */
(function () {
  'use strict';

  const init = () => {
    const root = document.querySelector('[data-collection-page]');
    if (!root) return;
    if (root.dataset.clientFilterInit === '1') return;
    root.dataset.clientFilterInit = '1';

    const baseUrl = root.dataset.collectionBase || window.location.pathname.replace(/\/[^/]*$/, '');

    const normalize = (s) =>
      (s || '').toString().trim().toLowerCase().replace(/\s+/g, '');

    let productsIndex = null;
    let fetchPromise = null;

    const fetchProducts = () => {
      if (productsIndex) return Promise.resolve(productsIndex);
      if (fetchPromise) return fetchPromise;
      const url = baseUrl.replace(/\/+$/, '') + '/products.json?limit=250';
      fetchPromise = fetch(url, { headers: { Accept: 'application/json' } })
        .then((res) => {
          if (!res.ok) throw new Error('products.json ' + res.status);
          return res.json();
        })
        .then((data) => {
          productsIndex = {};
          (data.products || []).forEach((p) => {
            const variants = p.variants || [];
            // Exclude sample variant (5 ml) from price calculation when there's any non-sample variant
            const realVariants = variants.filter((v) => {
              const t = normalize(v.title);
              return t !== '5ml';
            });
            const variantsForPrice = realVariants.length > 0 ? realVariants : variants;
            const prices = variantsForPrice
              .map((v) => parseFloat(v.price))
              .filter((n) => !isNaN(n) && n > 0);
            const formats = Array.from(new Set(variants.map((v) => normalize(v.title))));
            productsIndex[p.handle] = {
              // On conserve TOUS les prix, pas seulement les extremes. Un produit
              // vendu en 15 ml a 59 € et en 75 ml a 210 € n'a aucun prix entre les
              // deux : ne garder que le minimum et le maximum ferait croire qu'il
              // couvre toute la plage intermediaire.
              prices,
              minPrice: prices.length ? Math.min.apply(null, prices) : 0,
              maxPrice: prices.length ? Math.max.apply(null, prices) : 0,
              formats,
              available: variants.some((v) => v.available),
              vendor: p.vendor || '',
            };
          });
          return productsIndex;
        })
        .catch((e) => {
          console.error('[collection-filters] products.json fetch failed', e);
          productsIndex = {};
          return productsIndex;
        });
      return fetchPromise;
    };

    const updateCounts = (visible) => {
      const c = root.querySelector('.collection-page__count');
      if (c) {
        c.textContent =
          visible + ' parfum' + (visible !== 1 ? 's' : '') + ' trouvé' + (visible !== 1 ? 's' : '');
      }
      const h = root.querySelector('.collection-page__hero-count');
      if (h) {
        h.textContent = visible + ' parfum' + (visible !== 1 ? 's' : '');
      }
    };

    const toggleEmptyState = (showEmpty) => {
      let empty = root.querySelector('.collection-page__client-empty');
      const grid = root.querySelector('.collection-page__grid');
      if (!grid) return;
      if (showEmpty) {
        if (!empty) {
          empty = document.createElement('p');
          empty.className = 'collection-page__client-empty';
          empty.textContent = 'Aucun parfum ne correspond à ces filtres.';
          grid.parentElement.appendChild(empty);
        }
      } else if (empty) {
        empty.remove();
      }
    };

    // Nombre de parfums affichés par page (select "Afficher"). Défaut 50.
    const PERPAGE_ALLOWED = ['10', '25', '50', '100', 'all'];
    const getShowLimit = () => {
      const raw = new URLSearchParams(window.location.search).get('show');
      const v = PERPAGE_ALLOWED.indexOf(raw) !== -1 ? raw : '50';
      const sel = root.querySelector('[data-collection-perpage]');
      if (sel && sel.value !== v) sel.value = v;
      return v === 'all' ? Infinity : parseInt(v, 10);
    };

    // Page courante de la pagination client (par lot "Afficher")
    let currentPage = 1;

    const renderPagination = (totalPages) => {
      const nav = root.querySelector('[data-client-pagination]');
      if (!nav) return;
      if (totalPages <= 1) { nav.innerHTML = ''; nav.setAttribute('hidden', ''); return; }
      nav.removeAttribute('hidden');
      const info = (nav.dataset.labelPage || 'Page CURPAGE / NBPAGES')
        .replace('CURPAGE', currentPage).replace('NBPAGES', totalPages);
      const prevLbl = nav.dataset.labelPrev || 'Précédent';
      const nextLbl = nav.dataset.labelNext || 'Suivant';
      nav.innerHTML =
        '<button type="button" class="collection-page__page-link" data-cf-page="' + (currentPage - 1) + '"' + (currentPage <= 1 ? ' disabled' : '') + '>' + prevLbl + '</button>' +
        '<span class="collection-page__page-info">' + info + '</span>' +
        '<button type="button" class="collection-page__page-link" data-cf-page="' + (currentPage + 1) + '"' + (currentPage >= totalPages ? ' disabled' : '') + '>' + nextLbl + '</button>';
    };

    const apply = async () => {
      const limit = getShowLimit();
      const params = new URLSearchParams(window.location.search);
      const minPRaw = params.get('filter.v.price.gte');
      const maxPRaw = params.get('filter.v.price.lte');
      const minP = minPRaw == null || minPRaw === '' ? null : parseFloat(minPRaw);
      const maxP = maxPRaw == null || maxPRaw === '' ? null : parseFloat(maxPRaw);
      const formatsParam = params.getAll('filter.v.option.format').map(normalize).filter(Boolean);
      const availParam = params.getAll('filter.v.availability');

      const cards = root.querySelectorAll('.collection-page__grid > .collection-page__item');
      if (cards.length === 0) return;

      const hasFilter =
        (minP !== null && !isNaN(minP)) ||
        (maxP !== null && !isNaN(maxP)) ||
        formatsParam.length > 0 ||
        availParam.length > 0;

      // Construit la liste des cartes qui passent les filtres
      let matched;
      if (!hasFilter) {
        matched = Array.prototype.slice.call(cards);
      } else {
        const idx = await fetchProducts();
        matched = [];
        cards.forEach((card) => {
          const article = card.querySelector('[data-product-handle]') || card;
          const handle = article.dataset.productHandle || article.getAttribute('data-product-handle');
          const meta = handle && idx[handle];
          let show = true;
          if (meta) {
            // Le produit doit avoir UNE TAILLE dont le prix tombe dans la
            // fourchette. L'ancien test se contentait de verifier que la plage du
            // produit croisait celle du filtre : un parfum a 59 € et 210 €
            // ressortait sur une recherche 191-195 €, ou il n'a rien a proposer.
            // C'est aussi la regle appliquee par Shopify cote serveur, donc les
            // deux chemins donnent enfin la meme liste.
            if (minP !== null && !isNaN(minP) || maxP !== null && !isNaN(maxP)) {
              var bas = minP !== null && !isNaN(minP) ? minP : -Infinity;
              var haut = maxP !== null && !isNaN(maxP) ? maxP : Infinity;
              var tarifs = meta.prices && meta.prices.length ? meta.prices : [meta.minPrice, meta.maxPrice];
              if (!tarifs.some(function (prix) { return prix >= bas && prix <= haut; })) show = false;
            }
            if (formatsParam.length > 0) {
              const hasFormat = meta.formats.some((mf) => formatsParam.includes(mf));
              if (!hasFormat) show = false;
            }
            if (availParam.length > 0) {
              const wantIn = availParam.includes('1');
              const wantOut = availParam.includes('0');
              if (wantIn && !wantOut && !meta.available) show = false;
              if (wantOut && !wantIn && meta.available) show = false;
            }
          }
          if (show) matched.push(card);
        });
      }

      // Pagination : on n'affiche que le lot de la page courante
      const totalPages = limit === Infinity ? 1 : Math.max(1, Math.ceil(matched.length / limit));
      if (currentPage > totalPages) currentPage = totalPages;
      if (currentPage < 1) currentPage = 1;
      const start = limit === Infinity ? 0 : (currentPage - 1) * limit;
      const end = limit === Infinity ? matched.length : start + limit;
      const pageSet = new Set(matched.slice(start, end));
      cards.forEach((c) => { c.style.display = pageSet.has(c) ? '' : 'none'; });

      if (hasFilter) updateCounts(matched.length);
      toggleEmptyState(matched.length === 0);
      renderPagination(totalPages);
    };

    // Clic sur Précédent / Suivant → change de page (sans recharger)
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cf-page]');
      if (!btn || btn.hasAttribute('disabled')) return;
      e.preventDefault();
      const p = parseInt(btn.getAttribute('data-cf-page'), 10);
      if (isNaN(p)) return;
      currentPage = p;
      apply();
      const grid = root.querySelector('.collection-page__grid');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    /* ---------- Price slider init + AJAX-free filter ---------- */
    const initPriceSlider = (range) => {
      if (range.dataset.cfSliderInit === '1') return;
      range.dataset.cfSliderInit = '1';
      const min = parseInt(range.dataset.priceMin, 10) || 0;
      const max = parseInt(range.dataset.priceMax, 10) || 500;
      const lowSlider = range.querySelector('[data-price-slider="low"]');
      const highSlider = range.querySelector('[data-price-slider="high"]');
      const lowInput = range.querySelector('[data-price-input="low"]');
      const highInput = range.querySelector('[data-price-input="high"]');
      const fill = range.querySelector('[data-price-fill]');
      if (!lowSlider || !highSlider || !lowInput || !highInput || !fill) return;
      const span = max - min;
      if (span <= 0) return;

      // Read current URL params and prefill
      const urlParams = new URLSearchParams(window.location.search);
      const urlMin = urlParams.get('filter.v.price.gte');
      const urlMax = urlParams.get('filter.v.price.lte');
      if (urlMin) { lowSlider.value = urlMin; lowInput.value = urlMin; }
      if (urlMax) { highSlider.value = urlMax; highInput.value = urlMax; }

      const updateFill = () => {
        const lo = parseInt(lowSlider.value, 10);
        const hi = parseInt(highSlider.value, 10);
        const lp = ((lo - min) / span) * 100;
        const hp = ((hi - min) / span) * 100;
        fill.style.left = lp + '%';
        fill.style.width = Math.max(0, hp - lp) + '%';
      };
      const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

      // Update URL without page reload, then re-apply filter (instant, no AJAX)
      const updateUrl = () => {
        const lo = parseInt(lowSlider.value, 10);
        const hi = parseInt(highSlider.value, 10);
        const u = new URL(window.location.href);
        if (lo > min) u.searchParams.set('filter.v.price.gte', String(lo));
        else u.searchParams.delete('filter.v.price.gte');
        if (hi < max) u.searchParams.set('filter.v.price.lte', String(hi));
        else u.searchParams.delete('filter.v.price.lte');
        history.replaceState({}, '', u.toString());
        // Run filter immediately
        clearTimeout(window.__cfApplyTimer);
        apply();
      };

      let dragTimer = null;
      const debounced = () => {
        clearTimeout(dragTimer);
        dragTimer = setTimeout(updateUrl, 120);
      };

      lowSlider.addEventListener('input', () => {
        let v = parseInt(lowSlider.value, 10);
        const hi = parseInt(highSlider.value, 10);
        if (v >= hi) { v = Math.max(min, hi - 1); lowSlider.value = v; }
        lowInput.value = v <= min ? '' : v;
        updateFill();
        debounced();
      });
      highSlider.addEventListener('input', () => {
        let v = parseInt(highSlider.value, 10);
        const lo = parseInt(lowSlider.value, 10);
        if (v <= lo) { v = Math.min(max, lo + 1); highSlider.value = v; }
        highInput.value = v >= max ? '' : v;
        updateFill();
        debounced();
      });
      lowInput.addEventListener('input', () => {
        const v = lowInput.value === '' ? min : parseInt(lowInput.value, 10);
        if (!isNaN(v)) lowSlider.value = clamp(v, min, max);
        updateFill();
        debounced();
      });
      highInput.addEventListener('input', () => {
        const v = highInput.value === '' ? max : parseInt(highInput.value, 10);
        if (!isNaN(v)) highSlider.value = clamp(v, min, max);
        updateFill();
        debounced();
      });

      updateFill();
    };
    const initAllSliders = () => {
      root.querySelectorAll('[data-price-range]').forEach(initPriceSlider);
    };
    initAllSliders();

    // Sélecteur "Afficher N par page" → met à jour ?show= (replaceState patché → relance apply)
    root.addEventListener('change', (e) => {
      const sel = e.target.closest('[data-collection-perpage]');
      if (!sel) return;
      const v = sel.value;
      const u = new URL(window.location.href);
      if (v === '50') u.searchParams.delete('show');
      else u.searchParams.set('show', v);
      history.replaceState({}, '', u.toString());
    });

    // Run on initial load + on URL changes (popstate, AJAX-pushState mutations)
    apply();

    // Re-apply when grid content changes (after AJAX section swap)
    const obs = new MutationObserver(() => {
      initAllSliders();
      clearTimeout(window.__cfApplyTimer);
      window.__cfApplyTimer = setTimeout(apply, 60);
    });
    const grid = root.querySelector('.collection-page__grid');
    if (grid) obs.observe(grid, { childList: true });
    obs.observe(root, { childList: true });

    // popstate (back/forward)
    window.addEventListener('popstate', () => {
      currentPage = 1;
      setTimeout(apply, 80);
    });

    // Patch history.pushState/replaceState to fire our re-apply
    // (tout changement de filtre/affichage passe par là → on revient page 1)
    const origPush = history.pushState;
    history.pushState = function () {
      const r = origPush.apply(this, arguments);
      currentPage = 1;
      setTimeout(apply, 80);
      return r;
    };
    const origReplace = history.replaceState;
    history.replaceState = function () {
      const r = origReplace.apply(this, arguments);
      currentPage = 1;
      setTimeout(apply, 80);
      return r;
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
